/**
 * Framework-Agnostic Cookie Consent Core
 *
 * Owns the single source of truth for the client's consent state. Runs
 * before React boots so it works on BOTH layout families (Devise
 * `authentication.html.erb` for login/register/etc, and the React SPA
 * `application.html.erb`). React consumes this via the `ConsentProvider`
 * observer (see `app/components/consent/ConsentProvider.tsx`); ERB
 * templates consume it via `window.__consent__`. It owns the
 * `as_cookie_consent` / `as_anonymous_id` / `as_consent_synced` cookies and
 * every `/api/v1/consents` call.
 *
 * initConsent() branches:
 *   1. A valid current-version three-key cookie (`readConsentCookie()`):
 *      the visitor is decided. Categories come from the cookie with
 *      `advertising` forced off in memory under GPC (the cookie is not
 *      rewritten), `origin` comes from the cookie, `surface` is `none`. A
 *      background `GET /current` reconciles without the `reconciling` flag,
 *      and `cookie_sync` runs when the cookie was written on another origin.
 *   2. No cookie, or a stale-version / two-key one: the visitor is
 *      undecided with necessary-only categories and no cookie is minted
 *      (GPC never auto-decides; it only locks Advertising). A stale version
 *      sets `pendingSource = "version_bump_reprompt"`. `reconciling` is true
 *      while `GET /current` is in flight (1500 ms cap), and the
 *      resolved-defaults poll runs with its own deadline
 *      (`RESOLVED_DEFAULTS_DEADLINE_MS + RESOLVED_DEFAULTS_POLL_INTERVAL_MS`).
 *
 * `surface` is derived inside every `setState`: `none` once decided;
 * `pending` while `reconciling || !defaultsSettled`; `banner` when settled
 * with no resolved defaults; otherwise `surfaceForSeededCategories`.
 *
 * Guards: `decided` is set first thing in every decision path and is
 * re-checked by the seed callback, so a poll tick can never overwrite a
 * decision; `defaultsSeeded` makes the seed apply at most once per page,
 * whichever path (poll, immediate read, showCustomize re-read) wins.
 *
 * Dispatch contract (the GTM bootstrap in `gtm.ts` reads this): the
 * `setState` calls inside `initConsent()` (including a synchronous seed) do
 * not dispatch — read `getState()` after init; seeds dispatch while
 * `hasDecided` is false — never echo them as a decision.
 *
 * cookie_sync: a valid cookie whose `origin` is not this hostname (or is
 * missing) is POSTed once per `CONSENT_VERSION` with `source: cookie_sync`;
 * the host-only `as_consent_synced` marker is written only on success, and
 * a failure is logged and never blocks.
 */

import { CONSENT_VERSION } from "./version";
import {
  readConsentCookie,
  readConsentCookieVersion,
  readConsentSyncedMarker,
  writeConsentCookie,
  writeConsentSyncedMarker,
  ensureAnonymousId,
} from "./cookies";
import { detectGpc } from "./gpc";
import {
  computeInitialState,
  surfaceForSeededCategories,
  type ConsentSurface,
} from "./consentState";
import { lockAdvertisingUnderGpc } from "./gtmConsentMode";
import {
  readResolvedRegionalDefaults,
  subscribeToResolvedDefaults,
  RESOLVED_DEFAULTS_DEADLINE_MS,
  RESOLVED_DEFAULTS_POLL_INTERVAL_MS,
  type ResolvedRegionalDefaults,
} from "./resolvedDefaults";
import type { ConsentCategories, ConsentCookie } from "./types";
import {
  getCurrentConsent,
  postConsent,
  type ConsentApiRequest,
} from "../services/consentApi";

/**
 * Categories captured by this feature (`necessary` is always true).
 * Re-exported so callers keep importing them from the core.
 */
export type { ConsentCategories, ConsentSurface };

/**
 * Source values must mirror `ConsentLog::SOURCES` (Ruby). Used on every
 * saved decision so the audit log records how the decision was made.
 */
export type ConsentSource =
  | "banner_accept_all"
  | "banner_reject_all"
  | "banner_customize"
  | "notice_acknowledge"
  | "cookie_sync"
  | "version_bump_reprompt";

/**
 * Observable state snapshot.
 *
 * - `reconciling`: the initial `GET /api/v1/consents/current` is in flight
 *   for an undecided visitor; keeps `surface` at `pending`.
 * - `origin`: hostname the stored decision was written on (this hostname
 *   for an undecided visitor).
 * - `surface`: which first-visit surface to render (derived, never set).
 * - `defaultsSettled`: the region-resolved defaults were applied, the
 *   deadline elapsed, or the visitor is decided.
 * - `resolvedDefaults`: the raw resolved verdict (before the GPC lock), or
 *   `null` when nothing resolved.
 */
export interface ConsentState {
  categories: ConsentCategories;
  hasDecided: boolean;
  gpcActive: boolean;
  lastDecisionAt: string | null;
  consentVersion: string;
  pendingSource: ConsentSource | null;
  customizeOpen: boolean;
  reconciling: boolean;
  origin: string;
  surface: ConsentSurface;
  defaultsSettled: boolean;
  resolvedDefaults: ResolvedRegionalDefaults | null;
}

type Listener = (state: ConsentState) => void;

const NECESSARY_ONLY: ConsentCategories = {
  necessary: true,
  analytics: false,
  advertising: false,
};

// Max time to wait for the server reconciliation before allowing the
// banner to render anyway. Keeps a slow/failed network from suppressing
// the banner indefinitely.
const RECONCILE_TIMEOUT_MS = 1500;

// The settle gate's own deadline: the poll stops silently at its deadline,
// so one extra poll interval of margin before the banner is allowed.
const DEFAULTS_SETTLE_MS =
  RESOLVED_DEFAULTS_DEADLINE_MS + RESOLVED_DEFAULTS_POLL_INTERVAL_MS;

/**
 * The pre-init state.
 *
 * @returns a fresh undecided, unsettled state
 */
function freshState(): ConsentState {
  return Object.freeze({
    categories: NECESSARY_ONLY,
    hasDecided: false,
    gpcActive: false,
    lastDecisionAt: null,
    consentVersion: CONSENT_VERSION,
    pendingSource: null,
    customizeOpen: false,
    reconciling: false,
    origin: "",
    surface: "pending",
    defaultsSettled: false,
    resolvedDefaults: null,
  });
}

let state: ConsentState = freshState();
let listeners: Listener[] = [];

// Module guards, all reset by `__resetConsentCoreForTests()`.
let initialized = false;
// True while initConsent() runs: suppresses dispatch from the seed path.
let booting = false;
// The decidedRef: set first in every decision path, re-checked on seed.
let decided = false;
// Set once a resolved default is applied, by any path.
let defaultsSeeded = false;
let stopDefaults: (() => void) | null = null;
let deadlineTimer: ReturnType<typeof setTimeout> | null = null;
let reconcileTimer: ReturnType<typeof setTimeout> | null = null;
// Bumped on reset; async callbacks bail when it changed under them.
let generation = 0;

/**
 * Idempotent initializer. Safe to call more than once; subsequent calls
 * are no-ops. Does not dispatch `consent:change`.
 *
 * @returns the resolved state after synchronous initialization. The
 *   background reconcile, the defaults poll and the deadline may update
 *   state later and fire `consent:change` at that point.
 */
export function initConsent(): ConsentState {
  if (initialized) return state;
  initialized = true;
  booting = true;

  try {
    ensureAnonymousId();

    const gpc = detectGpc();
    const hostname = currentHostname();
    const cookie = readConsentCookie();
    const initial = computeInitialState({ cookie, gpcActive: gpc, hostname });

    if (cookie) {
      decided = true;
      setState({
        categories: initial.categories,
        hasDecided: true,
        gpcActive: gpc,
        consentVersion: cookie.version,
        pendingSource: null,
        reconciling: false,
        origin: initial.origin,
        defaultsSettled: true,
        resolvedDefaults: null,
      });
      reconcileWithServer();
      maybeSyncCookie(cookie, hostname);
      return state;
    }

    const storedVersion = readConsentCookieVersion();
    const stale = storedVersion !== null && storedVersion !== CONSENT_VERSION;
    setState({
      categories: initial.categories,
      hasDecided: false,
      gpcActive: gpc,
      consentVersion: CONSENT_VERSION,
      pendingSource: stale ? "version_bump_reprompt" : null,
      reconciling: true,
      origin: initial.origin,
      defaultsSettled: false,
      resolvedDefaults: null,
    });
    reconcileWithServer();
    startDefaultsWatch();
    return state;
  } finally {
    booting = false;
  }
}

/**
 * Read-only snapshot of the current state. The returned object is
 * frozen so listeners cannot mutate shared state.
 *
 * @returns the current `ConsentState`
 */
export function getState(): ConsentState {
  return state;
}

/**
 * Subscribe to state changes. The listener fires immediately with the
 * current state and then on every subsequent change.
 *
 * @param listener - called with each new state snapshot
 * @returns an unsubscribe function
 */
export function subscribe(listener: Listener): () => void {
  listeners.push(listener);
  listener(state);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

/**
 * Persist a consent decision: write the cookie, POST to the API, update
 * state, notify subscribers, and dispatch `consent:change` on `window`.
 * `advertising` is forced off under GPC.
 *
 * @param categories - the new category selection (necessary is forced true)
 * @param source - which surface produced the decision (banner, notice, ...)
 * @returns a promise that resolves after the API call completes; local
 *   state is updated synchronously before the promise settles so UI can
 *   unmount immediately.
 */
export async function save(
  categories: ConsentCategories,
  source: ConsentSource,
): Promise<void> {
  return persist(categories, source);
}

/**
 * Dismiss the notice by persisting the seeded categories as the visitor's
 * decision (`source: notice_acknowledge`), so the notice never returns.
 *
 * @returns a promise that resolves after the API call completes
 */
export async function acknowledge(): Promise<void> {
  return persist(state.categories, "notice_acknowledge");
}

/**
 * Open the Customize modal. No-op until the defaults have settled, so the
 * modal never snapshots categories that are about to be seeded. For an
 * undecided, not-yet-seeded visitor it first runs one guarded synchronous
 * re-read of the resolved defaults.
 */
export function showCustomize(): void {
  if (!state.defaultsSettled) return;
  if (!decided && !defaultsSeeded) {
    try {
      const resolved = readResolvedRegionalDefaults();
      if (resolved) {
        defaultsSeeded = true;
        clearDeadline();
        seedState(resolved);
      }
    } catch {
      // A throwing surface counts as "not resolved"; the modal still opens.
    }
  }
  setState({ customizeOpen: true });
  dispatchChange();
}

/**
 * Close the Customize modal without saving. Used by the Escape/Cancel
 * handlers in the vanilla modal and by tests.
 */
export function hideCustomize(): void {
  setState({ customizeOpen: false });
  dispatchChange();
}

// `detectGpc` is re-exported from ./gpc for tests and for callers that
// want the raw signal. `state.gpcActive` carries the same per-load value.
export { detectGpc } from "./gpc";

/**
 * Test-only reset hook so vitest can exercise `initConsent` multiple
 * times per file without needing to reload the module. Clears every
 * guard, timer and listener. Not used from production code.
 *
 * @returns void
 */
export function __resetConsentCoreForTests(): void {
  generation += 1;
  initialized = false;
  booting = false;
  decided = false;
  defaultsSeeded = false;
  stopDefaultsWatch();
  if (reconcileTimer !== null) {
    clearTimeout(reconcileTimer);
    reconcileTimer = null;
  }
  listeners = [];
  state = freshState();
}

/**
 * Merge a partial update into the current state, re-derive
 * `defaultsSettled` and `surface`, and freeze the result so listeners
 * cannot mutate it.
 *
 * @param partial - fields to overwrite on the current state
 */
function setState(partial: Partial<ConsentState>): void {
  const merged: ConsentState = { ...state, ...partial };
  merged.defaultsSettled = merged.hasDecided || merged.defaultsSettled;
  merged.surface = deriveSurface(merged);
  state = Object.freeze(merged);
}

/**
 * Which surface a state renders.
 *
 * @param s - the merged state
 * @returns the derived `ConsentSurface`
 */
function deriveSurface(s: ConsentState): ConsentSurface {
  if (s.hasDecided) return "none";
  if (s.reconciling || !s.defaultsSettled) return "pending";
  if (s.resolvedDefaults === null) return "banner";
  return surfaceForSeededCategories(s.categories);
}

/**
 * Dispatch unless initConsent() is still running.
 */
function notify(): void {
  if (!booting) dispatchChange();
}

/**
 * The current hostname, or "" outside a browser.
 *
 * @returns `window.location.hostname`
 */
function currentHostname(): string {
  return typeof window === "undefined" ? "" : window.location.hostname;
}

/**
 * Start the resolved-defaults poll and the settle deadline for an
 * undecided visitor. A throwing subscribe opens the gate immediately.
 */
function startDefaultsWatch(): void {
  deadlineTimer = setTimeout(() => {
    deadlineTimer = null;
    if (!decided && !state.defaultsSettled) {
      setState({ defaultsSettled: true });
      notify();
    }
  }, DEFAULTS_SETTLE_MS);

  try {
    stopDefaults = subscribeToResolvedDefaults(applySeed);
  } catch {
    clearDeadline();
    setState({ defaultsSettled: true });
  }
}

/**
 * Seed callback for the poll. Applies at most once and never after a
 * decision.
 *
 * @param resolved - the resolved regional defaults
 */
function applySeed(resolved: ResolvedRegionalDefaults): void {
  if (decided || defaultsSeeded) return;
  defaultsSeeded = true;
  clearDeadline();
  seedState(resolved);
  notify();
}

/**
 * Write the seeded categories (GPC lock applied) and the raw verdict.
 *
 * @param resolved - the resolved regional defaults
 */
function seedState(resolved: ResolvedRegionalDefaults): void {
  setState({
    categories: {
      necessary: true,
      analytics: resolved.analytics === true,
      advertising: lockAdvertisingUnderGpc(
        resolved.advertising === true,
        state.gpcActive,
      ),
    },
    resolvedDefaults: resolved,
    defaultsSettled: true,
  });
}

/** Clear the settle deadline. Idempotent. */
function clearDeadline(): void {
  if (deadlineTimer === null) return;
  clearTimeout(deadlineTimer);
  deadlineTimer = null;
}

/** Stop the poll and the settle deadline. Idempotent. */
function stopDefaultsWatch(): void {
  clearDeadline();
  if (stopDefaults) {
    stopDefaults();
    stopDefaults = null;
  }
}

/**
 * The shared decision path behind `save` and `acknowledge`.
 *
 * @param categories - the selection to persist
 * @param source - the audit source recorded on the row
 * @returns a promise that resolves after the API call completes
 */
async function persist(
  categories: ConsentCategories,
  source: ConsentSource,
): Promise<void> {
  decided = true;
  stopDefaultsWatch();

  const gpc = state.gpcActive;
  const finalCategories: ConsentCategories = {
    necessary: true,
    analytics: categories.analytics === true,
    advertising: gpc ? false : categories.advertising === true,
  };

  writeConsentCookie(finalCategories, CONSENT_VERSION, gpc);
  setState({
    categories: finalCategories,
    hasDecided: true,
    lastDecisionAt: new Date().toISOString(),
    consentVersion: CONSENT_VERSION,
    pendingSource: null,
    customizeOpen: false,
    origin: currentHostname(),
  });
  dispatchChange();

  const payload: ConsentApiRequest = {
    categories: finalCategories,
    source,
    consent_version: CONSENT_VERSION,
    gpc_detected: gpc,
  };
  await postConsent(payload);
}

/**
 * Normalize a category payload coming from an untrusted source (cookie,
 * server) into three strict booleans with `necessary` forced true.
 *
 * @param raw - the untrusted categories object
 * @returns a `ConsentCategories` with `necessary: true`
 */
function normalizeCategories(
  raw: { analytics?: unknown; advertising?: unknown } | undefined,
): ConsentCategories {
  return {
    necessary: true,
    analytics: raw?.analytics === true,
    advertising: raw?.advertising === true,
  };
}

/**
 * Notify all subscribers and dispatch a global `consent:change`
 * CustomEvent so non-subscriber listeners (analytics gates, vanilla
 * modules) can react too.
 */
function dispatchChange(): void {
  for (const listener of listeners.slice()) listener(state);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("consent:change", { detail: state }));
  }
}

/**
 * POST a cross-origin cookie once per `CONSENT_VERSION` as `cookie_sync`.
 * Never blocks; the marker is written only on success.
 *
 * @param cookie - the valid consent cookie read on this load
 * @param hostname - this page's hostname
 */
function maybeSyncCookie(cookie: ConsentCookie, hostname: string): void {
  if (cookie.origin === hostname) return;
  if (readConsentSyncedMarker() === CONSENT_VERSION) return;

  const gen = generation;
  const warn = (error: unknown): void => {
    console.warn("[consent] cookie_sync failed:", error);
  };
  void postConsent({
    categories: cookie.categories,
    source: "cookie_sync",
    consent_version: CONSENT_VERSION,
    gpc_detected: cookie.gpc,
  })
    .then((res) => {
      if (gen !== generation) return;
      if (res.ok) writeConsentSyncedMarker(CONSENT_VERSION);
      else warn(res.error);
    })
    .catch(warn);
}

/**
 * Kick off `GET /api/v1/consents/current` to reconcile with the
 * server-side row. Non-blocking: failures are ignored since the cookie is
 * authoritative for the decision.
 *
 * When the caller set `reconciling: true` (undecided init path), this
 * function clears it once the GET resolves; a `RECONCILE_TIMEOUT_MS`
 * fallback ensures a hung request never holds the surface at `pending`.
 * A newer current-version row becomes the decision.
 */
function reconcileWithServer(): void {
  const gen = generation;
  let settled = false;

  const finish = (updates: Partial<ConsentState>): void => {
    if (settled || gen !== generation) return;
    settled = true;
    // Skip the broadcast when nothing actually changes (e.g. valid-cookie
    // path with no remote row), so we don't fire spurious consent:change
    // events to React subscribers.
    const hasUpdates = Object.keys(updates).length > 0;
    if (!hasUpdates && !state.reconciling) return;
    setState({ ...updates, reconciling: false });
    dispatchChange();
  };

  reconcileTimer = setTimeout(() => {
    reconcileTimer = null;
    if (state.reconciling) finish({});
  }, RECONCILE_TIMEOUT_MS);

  void getCurrentConsent().then((result) => {
    if (gen !== generation) return;
    if (reconcileTimer !== null) {
      clearTimeout(reconcileTimer);
      reconcileTimer = null;
    }

    if (!result.ok || !result.data) {
      finish({});
      return;
    }
    if (result.data.consent_version !== CONSENT_VERSION) {
      finish({});
      return;
    }
    const remoteIsNewer =
      !state.lastDecisionAt || result.data.created_at > state.lastDecisionAt;
    if (!remoteIsNewer) {
      finish({});
      return;
    }
    decided = true;
    stopDefaultsWatch();
    const remote = normalizeCategories(result.data.categories);
    finish({
      categories: {
        ...remote,
        advertising: lockAdvertisingUnderGpc(
          remote.advertising,
          state.gpcActive,
        ),
      },
      hasDecided: true,
      lastDecisionAt: result.data.created_at,
      consentVersion: result.data.consent_version,
    });
  });
}

/**
 * Declaration for the global `__consent__` handle exposed to ERB
 * templates and the React observer. Attached in `exposeGlobal()`.
 */
declare global {
  interface Window {
    __consent__?: ConsentGlobal;
  }
}

/**
 * Public surface attached to `window.__consent__`. Mirrors the named
 * exports so ERB `onclick` handlers can call `showCustomize()` without
 * importing the module.
 */
export interface ConsentGlobal {
  initConsent: typeof initConsent;
  getState: typeof getState;
  subscribe: typeof subscribe;
  save: typeof save;
  acknowledge: typeof acknowledge;
  showCustomize: typeof showCustomize;
  hideCustomize: typeof hideCustomize;
}

/**
 * Attach the consent core to `window.__consent__`. Called from the
 * Vite entrypoint (`entrypoints/consent.ts`) so any ERB template or
 * non-React snippet can reach the same functions the React observer
 * uses.
 */
export function exposeGlobal(): void {
  if (typeof window === "undefined") return;
  window.__consent__ = {
    initConsent,
    getState,
    subscribe,
    save,
    acknowledge,
    showCustomize,
    hideCustomize,
  };
}
